import styled from '@emotion/styled';

import {
  spacing,
  borderRadius,
  typography,
  shadows,
  transitions,
  colors,
} from '../tokens';

export type GasLevel = 'low' | 'mid' | 'high';

/**
 * Paleta de cores por faixa de nível de gás.
 * Valores lidos dos prints do Figma (#27 verde, #28 vermelho, #29 amarelo).
 * Mantidos locais ao componente: divergem dos tokens globais.
 * Ver memória "design-token-spec-divergence".
 *
 * Thresholds (decisão PO):
 *  0–30%  → low   (vermelho)
 *  30–80% → mid   (amarelo)
 *  80–100%→ high  (verde)
 */
export const levelSpec: Record<
  GasLevel,
  {
    circleBg: string;
    circleColor: string;
    fill: string;
    badgeBg: string;
    badgeColor: string;
    progressText: string;
  }
> = {
  low: {
    circleBg: '#FFCCCC',
    circleColor: '#FF2929',
    fill: '#FF2929',
    badgeBg: '#FFCCCC',
    badgeColor: '#FF2929',
    progressText: '#3D3D3D',
  },
  mid: {
    circleBg: '#FFF5CC',
    circleColor: '#FFD633',
    fill: '#FFD633',
    badgeBg: '#FFF5CC',
    badgeColor: '#FFD633',
    progressText: '#3D3D3D',
  },
  high: {
    circleBg: '#E5FFEC',
    circleColor: '#33FF69',
    fill: '#33FF69',
    badgeBg: '#E5FFEC',
    badgeColor: '#33FF69',
    progressText: '#3D3D3D',
  },
};

// ─── Root ────────────────────────────────────────────────────────────────────

export const GasLevelMeterRoot = styled('div', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $error?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  width: 100%;
  background: ${colors.background};
  border-radius: ${borderRadius.lg};
  border: 1px solid ${({ $error }) => ($error ? colors.error : colors.border)};
  padding: ${spacing.lg};
  box-sizing: border-box;
  box-shadow: ${shadows.sm};
`;

// ─── Header ──────────────────────────────────────────────────────────────────

export const HeaderRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
`;

export const HeaderLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  flex: 1;
  min-width: 0;
`;

export const GasIconCircle = styled('span', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $level: GasLevel }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: ${borderRadius.full};
  background-color: ${({ $level }) => levelSpec[$level].circleBg};
  color: ${({ $level }) => levelSpec[$level].circleColor};
  transition: background-color ${transitions.base}, color ${transitions.base};

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const HeaderMeta = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const HeaderMetaRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

export const LevelSubtitle = styled('span')`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.regular};
  color: ${colors.textSecondary};
  white-space: nowrap;
`;

export const EditInputWrapper = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

export const EditInput = styled('input')`
  width: 68px;
  height: 32px;
  padding: 0 32px 0 12px;
  border: 1.5px solid #d0d0d0;
  border-radius: ${borderRadius.full};
  font-family: ${typography.fontFamily.primary};
  font-size: 16px;
  font-weight: ${typography.fontWeight.regular};
  color: #000000;
  background: #f2f2f2;
  outline: none;
  text-align: center;
  box-sizing: border-box;

  &:focus {
    border-color: #0b29fb;
    box-shadow: 0 0 0 2px rgba(11, 41, 251, 0.15);
  }

  /* remove setas do number input */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

export const EditInputCheckIcon = styled('span')`
  position: absolute;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: #0b29fb;
  pointer-events: none;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ConfirmButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: ${borderRadius.full};
  background: #0b29fb;
  color: #ffffff;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    opacity: 0.85;
  }
  &:focus-visible {
    outline: 2px solid #0b29fb;
    outline-offset: 2px;
  }
`;

export const LevelSeparator = styled('span')`
  display: inline-block;
  width: 1px;
  height: 20px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    transparent 48%,
    #8c8c8c 48%,
    #8c8c8c 52%,
    transparent 52%,
    transparent 100%
  );
  background-size: 1px 6px;
  background-repeat: repeat;
  margin: 0 ${spacing.xs};
  flex-shrink: 0;
  border-right: 1px dotted #8c8c8c;
`;

export const PencilButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: #0b29fb;
  cursor: pointer;
  border-radius: ${borderRadius.md};
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
  &:focus-visible {
    outline: 2px solid #0b29fb;
    outline-offset: 2px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const LevelBadge = styled('span', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $level: GasLevel }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: ${borderRadius.full};
  background-color: ${({ $level }) => levelSpec[$level].badgeBg};
  color: ${({ $level }) => levelSpec[$level].badgeColor};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.extraBold};
  line-height: 1;
  transition: background-color ${transitions.base}, color ${transitions.base};
`;

// ─── Progress bar ─────────────────────────────────────────────────────────────

export const ProgressTrack = styled('div')`
  position: relative;
  height: 36px;
  background: #f5f5f5;
  border-radius: ${borderRadius.full};
  overflow: hidden;
`;

export const ProgressFill = styled('div', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $value: number; $level: GasLevel }>`
  height: 100%;
  width: ${({ $value }) => $value}%;
  background-color: ${({ $level }) => levelSpec[$level].fill};
  border-radius: ${borderRadius.full};
  transition: width ${transitions.slow};
`;

/**
 * Zona bloqueada (85%→100%): faixa hachurada com linha tracejada no limite.
 * Comunica visualmente que o nível não pode ultrapassar o limite do tanque.
 * `$from` = posição inicial em % (ex.: 85).
 */
export const LimitZone = styled('div', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $from: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: ${({ $from }) => $from}%;
  background: repeating-linear-gradient(
    -45deg,
    rgba(140, 140, 140, 0.22) 0,
    rgba(140, 140, 140, 0.22) 6px,
    rgba(140, 140, 140, 0.08) 6px,
    rgba(140, 140, 140, 0.08) 12px
  );
  border-left: 2px dashed #8c8c8c;
  pointer-events: none;
`;

export const ProgressLabel = styled('span', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $level: GasLevel; $darkText: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: ${typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  pointer-events: none;
  color: ${({ $level }) => levelSpec[$level].progressText};
`;

export const EmptyHint = styled('p')`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  margin: 0;
  text-align: center;
  font-family: ${typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 700;
  color: #3d3d3d;
  pointer-events: none;
`;

// ─── Slider ───────────────────────────────────────────────────────────────────

export const SliderWrapper = styled('div')`
  position: relative;
  padding: ${spacing.xs} 0;
`;

/** Marca vertical no limite máximo (ex.: 85%) — o thumb não passa daqui. */
export const SliderLimitMark = styled('span', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $at: number }>`
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: ${({ $at }) => $at}%;
  width: 2px;
  transform: translateX(-50%);
  background: #8c8c8c;
  opacity: 0.6;
  pointer-events: none;
`;

export const StyledSlider = styled('input', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $background: string }>`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: ${borderRadius.full};
  background: ${({ $background }) => $background};
  outline: none;
  cursor: pointer;

  /* Webkit track */
  &::-webkit-slider-runnable-track {
    height: 6px;
    border-radius: ${borderRadius.full};
  }

  /* Webkit thumb — estilo alvo: anel escuro + gap branco + miolo escuro */
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    margin-top: -8px;
    border-radius: ${borderRadius.full};
    background: radial-gradient(
      circle,
      #3d3d3d 0%,
      #3d3d3d 42%,
      #ffffff 48%,
      #ffffff 100%
    );
    border: 2.5px solid #3d3d3d;
    box-shadow: ${shadows.sm};
    cursor: grab;
    transition: box-shadow ${transitions.fast}, transform ${transitions.fast};

    &:active {
      cursor: grabbing;
      transform: scale(1.1);
    }
  }

  /* Firefox */
  &::-moz-range-track {
    height: 6px;
    border-radius: ${borderRadius.full};
    background: transparent;
  }

  &::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: ${borderRadius.full};
    background: radial-gradient(
      circle,
      #3d3d3d 0%,
      #3d3d3d 42%,
      #ffffff 48%,
      #ffffff 100%
    );
    border: 2.5px solid #3d3d3d;
    box-shadow: ${shadows.sm};
    cursor: grab;
    transition: transform ${transitions.fast};
    &:active {
      cursor: grabbing;
      transform: scale(1.1);
    }
  }

  &::-moz-range-progress {
    height: 6px;
    border-radius: ${borderRadius.full};
  }

  &:focus-visible {
    &::-webkit-slider-thumb {
      box-shadow: 0 0 0 4px rgba(61, 61, 61, 0.2);
    }
    &::-moz-range-thumb {
      box-shadow: 0 0 0 4px rgba(61, 61, 61, 0.2);
    }
  }
`;

// ─── Labels ───────────────────────────────────────────────────────────────────

export const SliderLabels = styled('div')`
  position: relative;
  width: 100%;
  height: 16px;
`;

/**
 * Cada label é posicionado proporcionalmente na trilha:
 *  - start  → 0% (alinhado à esquerda)
 *  - end    → 100% (alinhado à direita)
 *  - center → em $left% da trilha, centralizado nesse ponto (ex: Sugerido 80%)
 */
export const SliderLabel = styled('span', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $align: 'start' | 'center' | 'end'; $left?: number }>`
  position: absolute;
  top: 0;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.regular};
  color: ${colors.textSecondary};
  white-space: nowrap;
  ${({ $align, $left }) => {
    if ($align === 'start') return 'left: 0;';
    if ($align === 'end') return 'right: 0;';
    return `left: ${$left ?? 50}%; transform: translateX(-50%);`;
  }}
`;
